/**
 * Tabla global de un módulo de nómina (una por pestaña de "Creditos y Movimientos").
 *
 * Es genérica: todo lo que cambia entre módulos viene en `definicion` (ver definicionesTablas.js).
 * Usa el `Table` de antd directo y NO `TableItem`, porque TableItem sólo respeta `defaultPageSize`
 * y no da scroll horizontal, y aquí hacen falta las dos cosas: la paginación la manda el servidor
 * y las tablas son anchas.
 */

import React, { Component } from 'react'
import axios from 'axios'
import { Table, Modal, message, Input, Switch, Tooltip, Dropdown, Menu } from 'antd'
import { connect } from 'react-redux'
import {
  ExclamationCircleOutlined, ReloadOutlined, PlusOutlined,
  DownloadOutlined, DownOutlined, FileExcelOutlined, FilePdfOutlined,
} from '@ant-design/icons'
import pdfMake from 'pdfmake/build/pdfmake'
import pdfFonts from 'pdfmake/build/vfs_fonts'
import { ButtonItem } from '../../../Components/Items'
import SelectEmpleadosItem from '../../../Components/SelectEmpleadosItem'
import creditosMovimientosPdf from '../../../Pdf/creditosMovimientosPdf'
import {
  construirTabla, descargarExcel, nombreArchivo, textoFecha,
} from './exportarCreditosMovimientos'

pdfMake.vfs = pdfFonts.pdfMake.vfs

const selectStyle = {
  padding: '3px 8px', border: '1px solid #d0d0d0', borderRadius: 4,
  fontSize: 12, background: 'white', cursor: 'pointer', height: 30,
}

const btnInline = {
  fontSize: 11, padding: '1px 8px', borderRadius: 4,
  border: '1px solid', cursor: 'pointer', background: 'white',
}

const Tarjeta = ({ etiqueta, valor, color }) => (
  <div style={{
    border: '1px solid #eee', borderRadius: 6, padding: '6px 14px',
    minWidth: 110, background: 'white',
  }}>
    <div style={{ fontSize: 10, color: '#999', textTransform: 'uppercase' }}>{etiqueta}</div>
    <div style={{ fontSize: 18, fontWeight: 600, color: color || '#333' }}>{valor}</div>
  </div>
)

class TablaGlobalNomina extends Component {

  state = {
    cargando: false,
    exportando: false,
    error: null,
    registros: [],
    total: 0,
    resumen: {},
    pagina: 1,
    porPagina: 50,
    // Filtros
    empleadoId: null,
    estatus: '',
    busqueda: '',
    soloVigentes: false,
    soloProblemas: false,
    extras: {},
  }

  temporizadorBusqueda = null

  componentDidMount() {
    this.cargar()
  }

  componentDidUpdate(prevProps) {
    // El contenedor sube este contador cuando el modal guardó algo, para que la tabla se refresque.
    if (prevProps.recargarToken !== this.props.recargarToken) this.cargar()
    if (prevProps.empresaId !== this.props.empresaId) this.cargar()
  }

  componentWillUnmount() {
    if (this.temporizadorBusqueda) clearTimeout(this.temporizadorBusqueda)
  }

  /**
   * Cuerpo de la consulta al endpoint del módulo, armado desde los filtros actuales.
   *
   * Lo usan tanto `cargar()` como `exportar()`: así el archivo no puede traer un recorte distinto
   * al de la pantalla. La exportación sólo le agrega `{ todos: true }`.
   */
  cuerpoConsulta(extra = {}) {
    const { empresaId } = this.props
    const {
      pagina, porPagina, empleadoId, estatus, busqueda,
      soloVigentes, soloProblemas, extras,
    } = this.state

    return {
      empresaId,
      empleadoId: empleadoId || undefined,
      // El backend espera un arreglo de números; '' = todos.
      estatus: estatus === '' ? undefined : [Number(estatus)],
      busqueda: busqueda || undefined,
      soloVigentes: soloVigentes || undefined,
      soloProblemas: soloProblemas || undefined,
      pagina,
      porPagina,
      ...extras,
      ...extra,
    }
  }

  async cargar() {
    const { definicion, empresaId, headersToken } = this.props

    if (!empresaId) return
    this.setState({ cargando: true, error: null })

    try {
      const res = await axios.post(
        definicion.endpointListado,
        this.cuerpoConsulta(),
        { headers: headersToken }
      )

      if (res.data.success === 2) {
        this.setState({
          registros: res.data.registros || [],
          total: res.data.total || 0,
          resumen: res.data.resumen || {},
        })
      } else {
        this.setState({ error: res.data.mensaje || res.data.err || 'Error al cargar el listado' })
      }
    } catch {
      this.setState({ error: 'Error de conexión al cargar el listado' })
    }
    this.setState({ cargando: false })
  }

  /** Cualquier cambio de filtro regresa a la página 1: si no, se ve una página vacía. */
  cambiarFiltro(parcial) {
    this.setState({ ...parcial, pagina: 1 }, () => this.cargar())
  }

  cambiarExtra(clave, valor) {
    this.setState(
      (prev) => ({ extras: { ...prev.extras, [clave]: valor || undefined }, pagina: 1 }),
      () => this.cargar()
    )
  }

  onBuscar(texto) {
    this.setState({ busqueda: texto })
    if (this.temporizadorBusqueda) clearTimeout(this.temporizadorBusqueda)
    this.temporizadorBusqueda = setTimeout(() => {
      this.setState({ pagina: 1 }, () => this.cargar())
    }, 400)
  }

  confirmarInactivar(registro) {
    const { definicion } = this.props
    Modal.confirm({
      title: definicion.tituloCancelar,
      content: definicion.textoCancelar,
      icon: <ExclamationCircleOutlined />,
      okText: 'Sí, continuar',
      cancelText: 'No',
      onOk: () => this.inactivar(registro),
    })
  }

  async inactivar(registro) {
    const { definicion, empresaId, headersToken } = this.props
    try {
      const res = await axios.post(
        definicion.endpointCancelar,
        definicion.cuerpoCancelar(registro, empresaId),
        { headers: headersToken }
      )
      if (res.data.success === 2) {
        message.success('Registro inactivado')
        this.cargar()
      } else {
        message.error(res.data.err || res.data.mensaje || 'Error al inactivar')
      }
    } catch {
      message.error('Error de conexión')
    }
  }

  // ── Exportación ────────────────────────────────────────────────────────────

  /**
   * Los filtros activos en texto, para el encabezado del Excel y del PDF: sin esto, un archivo
   * filtrado es indistinguible de uno completo.
   *
   * El nombre del empleado sale del primer registro y no de otra consulta: `SelectEmpleadosItem`
   * sólo devuelve el id, y si hay filtro por empleado todos los renglones son de ese empleado.
   */
  descripcionFiltros() {
    const { definicion, ctx } = this.props
    const { empleadoId, estatus, busqueda, soloVigentes, soloProblemas, extras, registros } = this.state
    const partes = []

    if (empleadoId) {
      partes.push(`Empleado: ${(registros[0] && registros[0].empleadoNombre) || 'seleccionado'}`)
    }
    if (estatus !== '') {
      const opcion = definicion.opcionesEstatus.find((o) => o.valor === estatus)
      if (opcion) partes.push(`Estatus: ${opcion.label}`)
    }

    const filtrosExtra = definicion.filtrosExtra ? definicion.filtrosExtra(ctx || {}) : []
    filtrosExtra.forEach((filtro) => {
      const valor = extras[filtro.clave]
      if (!valor) return
      const opcion = filtro.opciones.find((o) => String(o.valor) === String(valor))
      partes.push(opcion ? opcion.label : String(valor))
    })

    if (busqueda) partes.push(`Búsqueda: "${busqueda}"`)
    if (soloVigentes) partes.push('Sólo vigentes')
    if (soloProblemas && definicion.filtroProblemas) partes.push(definicion.filtroProblemas)

    return partes.length ? partes.join(' · ') : 'Sin filtros'
  }

  /** Trae TODO lo que cumple los filtros (no la página) y lo baja como Excel o PDF. */
  async exportar(formato) {
    const { definicion, ctx, empresaId, empresaNombre, headersToken } = this.props
    if (!empresaId || this.state.exportando) return

    this.setState({ exportando: true })
    try {
      const res = await axios.post(
        definicion.endpointListado,
        this.cuerpoConsulta({ todos: true }),
        { headers: headersToken }
      )

      if (res.data.success !== 2) {
        message.error(res.data.mensaje || res.data.err || 'Error al preparar la exportación')
        return
      }

      const registros = res.data.registros || []
      if (!registros.length) {
        message.warning('No hay registros que exportar con estos filtros')
        return
      }

      const descripcion = this.descripcionFiltros()

      if (formato === 'excel') {
        descargarExcel({
          definicion, ctx, registros, empresaNombre, descripcionFiltros: descripcion,
        })
      } else {
        const { columnas, filas, totales } = construirTabla({ definicion, ctx, registros })
        pdfMake
          .createPdf(creditosMovimientosPdf({
            titulo: definicion.titulo,
            empresaNombre,
            descripcionFiltros: descripcion,
            columnas,
            filas,
            totales,
            textoFecha,
          }))
          .download(`${nombreArchivo(definicion)}.pdf`)
      }
    } catch (e) {
      console.log(e)
      message.error('No se pudo generar el archivo')
    } finally {
      this.setState({ exportando: false })
    }
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  renderFiltros() {
    const { definicion, ctx, empresaId, headersToken, onAgregar } = this.props
    const { empleadoId, estatus, busqueda, soloVigentes, soloProblemas, extras } = this.state
    const filtrosExtra = definicion.filtrosExtra ? definicion.filtrosExtra(ctx || {}) : []

    return (
      <div style={{
        display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center',
        marginBottom: 12, padding: '10px 12px',
        background: '#fafcff', border: '1px solid #e6f0ff', borderRadius: 6,
      }}>
        <div style={{ width: 260 }}>
          <SelectEmpleadosItem
            empresaId={empresaId}
            headersToken={headersToken}
            value={empleadoId || undefined}
            onChange={(valor) => this.cambiarFiltro({ empleadoId: valor || null })}
            allowClear
            width="100%"
            dropdownMatchSelectWidth={500}
            placeholder="Todos los empleados"
          />
        </div>

        <select
          style={selectStyle}
          value={estatus}
          onChange={(e) => this.cambiarFiltro({ estatus: e.target.value })}
        >
          <option value="">Todos los estatus</option>
          {definicion.opcionesEstatus.map((o) => (
            <option key={o.valor} value={o.valor}>{o.label}</option>
          ))}
        </select>

        {filtrosExtra.map((f) => (
          <select
            key={f.clave}
            style={{ ...selectStyle, maxWidth: f.ancho || 200 }}
            value={extras[f.clave] || ''}
            onChange={(e) => this.cambiarExtra(f.clave, e.target.value)}
          >
            <option value="">{f.placeholder}</option>
            {f.opciones.map((o) => (
              <option key={o.valor} value={o.valor}>{o.label}</option>
            ))}
          </select>
        ))}

        <Input.Search
          style={{ width: 220 }}
          size="small"
          allowClear
          placeholder="Buscar…"
          value={busqueda}
          onChange={(e) => this.onBuscar(e.target.value)}
          onSearch={() => this.setState({ pagina: 1 }, () => this.cargar())}
        />

        {definicion.soportaVigentes && (
          <Tooltip title={definicion.ayudaVigentes}>
            <span style={{ fontSize: 12, color: '#666', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Switch
                size="small"
                checked={soloVigentes}
                onChange={(v) => this.cambiarFiltro({ soloVigentes: v })}
              />
              Solo vigentes
            </span>
          </Tooltip>
        )}

        {definicion.filtroProblemas && (
          <Tooltip title="Un crédito sin fecha de aplicación nunca entra al cálculo de la nómina">
            <span style={{ fontSize: 12, color: '#cf1322', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <Switch
                size="small"
                checked={soloProblemas}
                onChange={(v) => this.cambiarFiltro({ soloProblemas: v })}
              />
              {definicion.filtroProblemas}
            </span>
          </Tooltip>
        )}

        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          <Dropdown
            trigger={['click']}
            overlay={(
              <Menu>
                <Menu.Item
                  key="excel"
                  icon={<FileExcelOutlined />}
                  onClick={() => this.exportar('excel')}
                >
                  Excel
                </Menu.Item>
                <Menu.Item
                  key="pdf"
                  icon={<FilePdfOutlined />}
                  onClick={() => this.exportar('pdf')}
                >
                  PDF
                </Menu.Item>
              </Menu>
            )}
          >
            {/* Baja todo lo que cumple los filtros, no sólo la página en pantalla. */}
            <ButtonItem size="small" blanco loading={this.state.exportando} icon={<DownloadOutlined />}>
              Exportar <DownOutlined />
            </ButtonItem>
          </Dropdown>
          <ButtonItem size="small" blanco icon={<ReloadOutlined />} onClick={() => this.cargar()}>
            Actualizar
          </ButtonItem>
          <ButtonItem size="small" type="primary" icon={<PlusOutlined />} onClick={onAgregar}>
            Agregar
          </ButtonItem>
        </div>
      </div>
    )
  }

  renderTarjetas() {
    const { definicion } = this.props
    const { resumen, total } = this.state
    return (
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', marginBottom: 12 }}>
        {/* `total` respeta todos los filtros; las demás tarjetas ignoran el de estatus a
            propósito, para que no se vacíen justo cuando filtras por uno de ellos. */}
        <Tarjeta etiqueta="Con estos filtros" valor={total} />
        {definicion.opcionesEstatus.map((o) => (
          <Tarjeta
            key={o.valor}
            etiqueta={o.label}
            valor={resumen[o.valor] || 0}
            color={definicion.etiquetasEstatus[o.valor]?.color === 'green' ? '#389e0d' : undefined}
          />
        ))}
      </div>
    )
  }

  columnasConAcciones() {
    const { definicion, ctx, onAbrir } = this.props
    const puedeCancelar = definicion.puedeCancelar || ((r) => r.estatus === 1)

    return [
      ...definicion.columnas(ctx || {}),
      {
        title: 'Acciones',
        key: 'acciones',
        fixed: 'right',
        width: 150,
        render: (_, registro) => (
          <div style={{ display: 'flex', gap: 6 }}>
            <button type="button"
              style={{ ...btnInline, borderColor: '#1677ff', color: '#1677ff' }}
              onClick={() => onAbrir(registro)}
            >
              Abrir
            </button>
            {puedeCancelar(registro) && (
              <button type="button"
                style={{ ...btnInline, borderColor: '#ff4d4f', color: '#ff4d4f' }}
                onClick={() => this.confirmarInactivar(registro)}
              >
                Inactivar
              </button>
            )}
          </div>
        ),
      },
    ]
  }

  render() {
    const { definicion } = this.props
    const { cargando, error, registros, total, pagina, porPagina } = this.state

    return (
      <>
        {this.renderFiltros()}
        {this.renderTarjetas()}

        {error && (
          <div style={{ color: '#cf1322', fontSize: 12, marginBottom: 8 }}>{error}</div>
        )}

        <Table
          rowKey="_id"
          size="small"
          bordered
          loading={cargando}
          columns={this.columnasConAcciones()}
          dataSource={registros}
          scroll={{ x: definicion.anchoTabla }}
          onRow={(registro) => ({
            onDoubleClick: () => this.props.onAbrir(registro),
            // Mismo código de color que las tablas del Sobre Recibo: gris tachado lo inactivo,
            // azul claro lo completado.
            style: {
              cursor: 'pointer',
              background: registro.estatus === 0 ? '#fafafa' : registro.estatus === 2 ? '#f0f5ff' : undefined,
              color: registro.estatus === 0 ? '#999' : undefined,
            },
          })}
          pagination={{
            current: pagina,
            pageSize: porPagina,
            total,
            showSizeChanger: true,
            pageSizeOptions: ['25', '50', '100', '200'],
            showTotal: (t, rango) => `${rango[0]}-${rango[1]} de ${t}`,
            onChange: (p, tam) =>
              this.setState({ pagina: p, porPagina: tam }, () => this.cargar()),
          }}
          locale={{ emptyText: 'No hay registros con estos filtros' }}
        />
      </>
    )
  }
}

const mapStateToProps = (state) => ({
  headersToken: state.user.headersToken,
  empresaId: state.empresa.id,
  empresaNombre: state.empresa.nombre,
})

export default connect(mapStateToProps)(TablaGlobalNomina)
